import { WHATSAPP_NUMBER } from "@/lib/whatsapp";
import { Link } from "react-router-dom";
import InstallAppButton from "@/components/InstallAppButton";

/**
 * Myntra-style utility footer — white bg, dense link lists, pink
 * accents, and a compact "A Brand of Apex Enterprises" credit pill.
 */
const Footer = ({ categories = [] }) => (
  <footer className="mt-16 border-t border-line bg-white" data-testid="site-footer">
    <div className="lux-container py-10 grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-6">
      {/* Online Shopping */}
      <div className="md:col-span-3">
        <p className="text-[13px] font-bold text-ink uppercase mb-4 tracking-wider">
          Online Shopping
        </p>
        <ul className="space-y-2.5 text-[13px] text-ink-soft">
          {categories.slice(0, 6).map((c) => (
            <li key={c.id}>
              <Link to={`/category/${c.slug}`} className="hover:text-pink transition-colors">
                {c.name}
              </Link>
            </li>
          ))}
          <li><Link to="/new-arrivals/women" className="hover:text-pink transition-colors">New Arrivals</Link></li>
          <li><Link to="/collections" className="hover:text-pink transition-colors">All Collections</Link></li>
        </ul>
      </div>

      {/* Customer Policies */}
      <div className="md:col-span-3">
        <p className="text-[13px] font-bold text-ink uppercase mb-4 tracking-wider">
          Customer Policies
        </p>
        <ul className="space-y-2.5 text-[13px] text-ink-soft">
          <li><Link to="/contact" className="hover:text-pink transition-colors">Contact Us</Link></li>
          <li><Link to="/faq" className="hover:text-pink transition-colors">FAQ</Link></li>
          <li><Link to="/terms" className="hover:text-pink transition-colors" data-testid="footer-terms-link">T&amp;C</Link></li>
          <li><Link to="/terms" className="hover:text-pink transition-colors">Terms of Use</Link></li>
          <li><Link to="/shipping" className="hover:text-pink transition-colors" data-testid="footer-shipping-link">Shipping</Link></li>
          <li><Link to="/track-order" className="hover:text-pink transition-colors" data-testid="footer-track-link">Track Your Order</Link></li>
          <li><Link to="/returns" className="hover:text-pink transition-colors" data-testid="footer-returns-link">Cancellation &amp; Returns</Link></li>
          <li><Link to="/privacy" className="hover:text-pink transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
        </ul>
      </div>

      {/* Experience Luxsova */}
      <div className="col-span-2 md:col-span-3">
        <p className="text-[13px] font-bold text-ink uppercase mb-4 tracking-wider">
          Experience Luxsova
        </p>
        <div className="mb-5" data-testid="footer-install-slot">
          <InstallAppButton variant="menu" />
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello Luxsova team 👋 I have a question about your pieces.")}`}
          target="_blank"
          rel="noreferrer"
          className="btn-whatsapp inline-flex items-center gap-2 !text-[11px] !py-2.5 !px-4"
          data-testid="footer-whatsapp"
        >
          WhatsApp Concierge
        </a>
        <p className="text-[13px] text-ink-soft mt-5">
          <a href="mailto:care@luxsova.com" className="hover:text-pink transition-colors" data-testid="footer-email">
            care@luxsova.com
          </a>
        </p>
        <p className="text-[13px] text-ink-soft mt-1">
          <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="hover:text-pink transition-colors">
            +91 87188 84698
          </a>
        </p>
        <p className="text-[13px] text-ink-soft mt-1">
          <span className="font-semibold text-ink">Registered address:</span><br />
          Apex Enterprises, Dhamankar Naka Flyover,<br />
          Gala No. 17, Arihant Darshan Building,<br />
          Bhiwandi, Mumbai, Maharashtra 421302, India
        </p>
      </div>

      {/* Trust badges */}
      <div className="col-span-2 md:col-span-3">
        <p className="text-[13px] font-bold text-ink uppercase mb-4 tracking-wider">
          Keep in Touch
        </p>
        <ul className="space-y-2.5 text-[13px] text-ink-soft mb-6">
          <li><Link to="/reviews" className="hover:text-pink transition-colors">Reviews</Link></li>
          <li><Link to="/about" className="hover:text-pink transition-colors">About</Link></li>
        </ul>

        <Link to="/about" className="flex items-start gap-3 mb-4 hover:opacity-80 transition-opacity">
          <div className="w-9 h-9 rounded-full bg-bg-2 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-greenbrand" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
          </div>
          <div>
            <p className="text-[12px] font-bold text-ink uppercase tracking-wider">Handpicked Luxury</p>
            <p className="text-[11px] text-ink-mute leading-relaxed">Every piece curated by our team at Luxsova</p>
          </div>
        </Link>
        <Link to="/returns" className="flex items-start gap-3 hover:opacity-80 transition-opacity" data-testid="footer-return-exchange-badge">
          <div className="w-9 h-9 rounded-full bg-bg-2 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-pink" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </div>
          <div>
            <p className="text-[12px] font-bold text-ink uppercase tracking-wider">Return &amp; Exchange</p>
            <p className="text-[11px] text-ink-mute leading-relaxed">Easy 7-day return &amp; exchange policy</p>
          </div>
        </Link>
      </div>
    </div>

    {/* Bottom credit rows */}
    <div className="border-t border-line bg-bg-2">
      <div className="lux-container py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-ink-mute">
        <span>© {new Date().getFullYear()} www.luxsova.com. All rights reserved.</span>
        <span className="uppercase tracking-wider font-semibold">
          Crafted in India · Shipped worldwide
        </span>
      </div>
      <div className="lux-container pb-8 md:pb-6 pt-2 flex flex-col items-center justify-center mb-20 md:mb-0" data-testid="footer-parent-credit">
        <span className="apex-badge">A Brand of Apex Enterprises</span>
      </div>
    </div>
  </footer>
);

export default Footer;
